import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const defaultInvoice = () => {
  return {
    _id: '_new',
    number: '_new',
    invoiceType: 'RECEIVABLE',
    invoiceState: 'DRAFT',
    invoiceNumber: '',
    grossAmount: null,
    netAmount: null,
    taxAmount: null,
    invoiceDate: null,
    dueDate: null,
    paidDate: null,
    project: null,
    supplier: null,
    notes: '',
    attachments: [],
    createdBy: null,
  };
};

const getDefaultState = () => {
  return {
    invoices: [],
    invoiceCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an invoice list view
 * used for paginated invoice queries
 * @type {DocumentNode}
 */
const INVOICE_LIST_REQUEST_FIELDS = gql`
  fragment invoiceListFields on Invoice {
    _id
    number
    invoiceType
    invoiceState
    invoiceNumber
    grossAmount
    netAmount
    invoiceDate
    dueDate
    paidDate
    supplier {
      _id
      number
      name
    }
    project {
      id
      number
      customer {
        firstname
        lastname
      }
    }
    createdBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

/**
 * Defines the fields which are requested for an invoice
 * used for all queries/mutations which return an invoice
 * @type {DocumentNode}
 */
const INVOICE_REQUEST_FIELDS = gql`
  fragment invoiceFields on Invoice {
    _id
    number
    invoiceType
    invoiceState
    invoiceNumber
    grossAmount
    netAmount
    taxAmount
    invoiceDate
    dueDate
    paidDate
    notes
    attachments {
      id
      path
      filename
      displayFilename
      size
      mimetype
      attachmentType
    }
    attachmentTypes
    supplier {
      _id
      number
      name
    }
    project {
      id
      number
      customer {
        firstname
        lastname
        street
        streetNumber
        streetNumberSuffix
        city
        zip
      }
    }
    createdBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} invoice
 */
const createPayload = (invoice) => {
  const payload = {
    invoiceType: invoice.invoiceType,
    invoiceState: invoice.invoiceState,
    invoiceNumber: invoice.invoiceNumber,
    grossAmount: invoice.grossAmount,
    invoiceDate: invoice.invoiceDate,
  };

  // Add optional fields only if they have values
  if (invoice.netAmount !== null && invoice.netAmount !== undefined) {
    payload.netAmount = invoice.netAmount;
  }
  if (invoice.taxAmount !== null && invoice.taxAmount !== undefined) {
    payload.taxAmount = invoice.taxAmount;
  }
  if (invoice.dueDate) {
    payload.dueDate = invoice.dueDate;
  }
  if (invoice.paidDate) {
    payload.paidDate = invoice.paidDate;
  }
  if (invoice.project) {
    payload.project = invoice.project.id || invoice.project._id || invoice.project;
  }
  if (invoice.supplier) {
    payload.supplier = invoice.supplier._id || invoice.supplier;
  }
  if (invoice.notes) {
    payload.notes = invoice.notes;
  }

  return payload;
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered invoice list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchInvoicesPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query invoicesPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: InvoiceFilterInput
          ) {
            invoicesPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
              invoices {
                ...invoiceListFields
              }
              invoiceCount
            }
          }
          ${INVOICE_LIST_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      commit('SET_INVOICES', response.data.invoicesPaginated.invoices);
      commit('SET_INVOICE_COUNT', response.data.invoicesPaginated.invoiceCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  async fetchInvoiceByNumber({ commit, dispatch }, invoiceNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query invoiceByNumber($invoiceNumber: Int!) {
            invoiceByNumber(invoiceNumber: $invoiceNumber) {
              ...invoiceFields
            }
          }
          ${INVOICE_REQUEST_FIELDS}
        `,
        variables: {
          invoiceNumber: parseInt(invoiceNumber),
        },
      });
      commit('ADD_OR_UPDATE_INVOICE', response.data.invoiceByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async createInvoice({ commit, dispatch }, invoice) {
    const payload = createPayload(invoice);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createInvoice($payload: InvoiceInput!) {
            createInvoice(invoiceInput: $payload) {
              ...invoiceFields
            }
          }
          ${INVOICE_REQUEST_FIELDS}
        `,
        variables: {
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Rechnung gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_INVOICE', '_new'); // remove temporary _new invoice
      commit('ADD_OR_UPDATE_INVOICE', response.data.createInvoice);
      return response.data.createInvoice.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async updateInvoice({ commit, dispatch }, invoice) {
    const payload = createPayload(invoice);
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateInvoice($invoiceNumber: Int!, $payload: InvoiceInput!) {
            updateInvoice(invoiceNumber: $invoiceNumber, invoiceInput: $payload) {
              ...invoiceFields
            }
          }
          ${INVOICE_REQUEST_FIELDS}
        `,
        variables: {
          invoiceNumber: invoice.number,
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Rechnung aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_INVOICE', response.data.updateInvoice);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async deleteInvoice({ commit, dispatch }, invoiceNumber) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteInvoice($invoiceNumber: Int!) {
            deleteInvoice(invoiceNumber: $invoiceNumber)
          }
        `,
        variables: {
          invoiceNumber: invoiceNumber,
        },
      });
      dispatch('message', {
        message: 'Rechnung gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_INVOICE', invoiceNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new invoice object in store and return it
   * @param {*} param0
   * @param {*} invoice
   */
  async initInvoice({ commit }) {
    commit('ADD_OR_UPDATE_INVOICE', defaultInvoice());
  },

  invoiceLocalUpdate({ commit }, invoice) {
    commit('ADD_OR_UPDATE_INVOICE', invoice);
  },

  invoiceCleanup({ commit }) {
    commit('REMOVE_INVOICE', '_new');
  },

  /**
   * Upload an attachment to an invoice
   * @param {*} param0
   * @param {*} payload - contains invoiceId, file, attachmentType
   */
  async uploadInvoiceAttachment({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation uploadInvoiceAttachment($attachmentInput: AttachmentWithTypeInput) {
            uploadInvoiceAttachment(attachmentInput: $attachmentInput) {
              ...invoiceFields
            }
          }
          ${INVOICE_REQUEST_FIELDS}
        `,
        variables: {
          attachmentInput: {
            referenceId: payload.parentId,
            referenceType: payload.parentType,
            file: payload.file,
          },
        },
      });
      commit('ADD_OR_UPDATE_INVOICE', response.data.uploadInvoiceAttachment);
      dispatch('message', {
        message: 'Anhang hochgeladen!',
        type: 'success',
        lifetime: 2,
      });
      return response.data.uploadInvoiceAttachment;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim hochladen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Delete an attachment from an invoice
   * @param {*} param0
   * @param {*} payload - contains invoiceId, attachmentId
   */
  async deleteInvoiceAttachment({ commit, dispatch }, { invoiceId, attachmentId }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteInvoiceAttachment($invoiceId: ID!, $attachmentId: ID!) {
            deleteInvoiceAttachment(invoiceId: $invoiceId, attachmentId: $attachmentId) {
              ...invoiceFields
            }
          }
          ${INVOICE_REQUEST_FIELDS}
        `,
        variables: {
          invoiceId,
          attachmentId,
        },
      });
      commit('ADD_OR_UPDATE_INVOICE', response.data.deleteInvoiceAttachment);
      dispatch('message', {
        message: 'Anhang gelöscht!',
        type: 'success',
        lifetime: 2,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_INVOICE_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_INVOICES: (state, invoices) =>
    invoices ? (state.invoices = invoices) : (state.invoices = []),
  ADD_INVOICE: (state, invoice) => state.invoices.push(invoice),
  ADD_OR_UPDATE_INVOICE: (state, updatedInvoice) => {
    const existingInvoice = state.invoices.find((invoice) => invoice._id === updatedInvoice._id);
    if (existingInvoice) {
      Object.assign(existingInvoice, updatedInvoice);
    } else {
      state.invoices.push(updatedInvoice);
    }
  },
  REMOVE_INVOICE: (state, invoiceId) => {
    const index = state.invoices.findIndex(
      (invoice) => invoice._id === invoiceId || invoice.number === invoiceId
    );
    if (index !== -1) {
      state.invoices.splice(index, 1);
    }
  },
  UPDATE_INVOICE: (state, updatedInvoice) => {
    const invoice = state.invoices.find((invoice) => invoice._id === updatedInvoice._id);
    Object.assign(invoice, updatedInvoice);
  },
  SET_INVOICE_COUNT: (state, invoiceCount) => (state.invoiceCount = invoiceCount),
};

/**
 * Getters
 */
const getters = {
  getInvoices: (state) => state.invoices,
  getInvoice: (state) => (invoiceId) => state.invoices.find((invoice) => invoice._id == invoiceId),
  getInvoicesByState: (state) => (invoiceState) =>
    state.invoices.filter((invoice) => invoice.invoiceState == invoiceState),
  getInvoicesByType: (state) => (invoiceType) =>
    state.invoices.filter((invoice) => invoice.invoiceType == invoiceType),
  getInvoiceByNumber: (state) => (invoiceNumber) =>
    state.invoices.find((invoice) => invoice.number == invoiceNumber),
  getInvoiceCount: (state) => state.invoiceCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
